import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    DocumentTemplateDto,
    FormValuesDto,
    DocumentInstanceDto,
    DocumentTemplateSearchDto,
    DocumentTemplateListResponseDto,
    PrefilledFormDto,
    OptionDto,
    GenerateDocumentValidationFailedDto,
    GenerationValidationErrorDto,
} from '../document-generation.dto';
import { admiDocumentGenerationService } from '../admi-document-generation.service';
import { admiDataCatalogService } from '../admi-data-catalog.service';
import { isProblemDetail, isProblemDetailCode, isProblemDetailTextOr, ProblemDetail } from 'src/common/ProblemDetail';
import { Building, LocationSearch } from 'src/features/real-estate/buildings.dto';
import { admiBuildingsService } from 'src/features/real-estate/admi-buildings.service';

export enum GenerationStatus {
    GENERATING,
    SUCCESS,
    FAILED,
}

export interface State {
    activeStep: number;
    errorMessage: string | null;

    templatesResponse: DocumentTemplateListResponseDto | null;
    selectedTemplate: DocumentTemplateDto | null;

    buildings: Building[];
    selectedBuildings: Building[] | null;

    generationStatus: GenerationStatus;
    generationResponse: DocumentInstanceDto | null;
    generationError: ProblemDetail | null;
    validationErrors: GenerationValidationErrorDto[] | null;
    prefilledForm: PrefilledFormDto | null;
    dependencyGraph: Record<string, string[]> | null;
    formValues: FormValuesDto | null;
    imageFieldMap: Record<string, string[]>;
    dynamicOptions: Record<string, OptionDto[]>;
}

export const initialState: State = {
    activeStep: 0,
    errorMessage: null,
    templatesResponse: null,
    selectedTemplate: null,
    buildings: [],
    selectedBuildings: null,
    generationStatus: GenerationStatus.GENERATING,
    generationError: null,
    validationErrors: null,
    generationResponse: null,
    prefilledForm: null,
    dependencyGraph: null,
    formValues: null,
    imageFieldMap: {},
    dynamicOptions: {},
};

export const loadDependencyGraph = createAsyncThunk(
    'documentGenerationNewDocument/loadDependencyGraph',
    async (keys: string[], thunkApi) => {
        try {
            return await admiDataCatalogService.resolveFields(keys, {}, true);
        } catch (err) {
            return thunkApi.rejectWithValue(err);
        }
    },
);

export const loadDocumentTemplates = createAsyncThunk(
    'documentGenerationNewDocument/loadDocumentTemplates',
    async (search: DocumentTemplateSearchDto, thunkApi) => {
        try {
            return await admiDocumentGenerationService.getDocumentTemplates(search);
        } catch (err: unknown) {
            return thunkApi.rejectWithValue(err);
        }
    },
);

export const loadBuildings = createAsyncThunk(
    'documentGenerationNewDocument/loadBuildings',
    async (search: LocationSearch, thunkApi) => {
        try {
            return await admiBuildingsService.loadBuildingsData(search);
        } catch (err: unknown) {
            return thunkApi.rejectWithValue(err);
        }
    },
);

export const submitForm = createAsyncThunk<
    DocumentInstanceDto,
    FormValuesDto,
    { state: { documentGenerationNewDocument: State } }
>('documentGenerationNewDocument/submitForm', async (formValues: FormValuesDto, thunkApi) => {
    try {
        const state = thunkApi.getState();
        const templateId = state.documentGenerationNewDocument.selectedTemplate!.id!;
        const buildings = state.documentGenerationNewDocument.selectedBuildings!;
        const buildingId = buildings[0]?.id;

        const sanitizedFormValues = Object.fromEntries(
            Object.entries(formValues).map(([k, v]) => [
                k,
                (typeof v === 'string' && v.trim() === '') || v === undefined ? null : v,
            ]),
        );

        const extendedValues = {
            ...sanitizedFormValues,
            buildingId,
        };

        return await admiDocumentGenerationService.generateDocument({
            templateId: templateId,
            values: extendedValues,
        });
    } catch (err) {
        return thunkApi.rejectWithValue(err);
    }
});

export const getPrefilledForm = createAsyncThunk<
    PrefilledFormDto,
    void,
    { state: { documentGenerationNewDocument: State } }
>('documentGenerationNewDocument/getPrefilledForm', async (_, thunkApi) => {
    try {
        const state = thunkApi.getState();
        const templateId = state.documentGenerationNewDocument.selectedTemplate?.id;
        const buildings = state.documentGenerationNewDocument.selectedBuildings;

        if (!templateId) {
            throw new Error('Template ID is missing');
        }

        if (!buildings || buildings.length !== 1) {
            throw new Error('Exactly one building must be selected');
        }
        const context = {
            buildingId: buildings[0].id,
            buildingProductId: buildings[0].dataProductId || '',
        };
        const response = await admiDocumentGenerationService.getPrefilledForm(templateId, context);
        return response;
    } catch (err) {
        return thunkApi.rejectWithValue(err);
    }
});

const documentGenerationNewDocumentSlice = createSlice({
    name: 'documentGenerationNewDocument',
    initialState,
    reducers: {
        resetState: () => initialState,
        goBack: (state) => {
            if (state.activeStep > 0) {
                state.activeStep--;
            }
        },
        selectTemplate: (state, action: PayloadAction<DocumentTemplateDto>) => {
            state.selectedTemplate = action.payload;
            state.formValues = null;
            state.imageFieldMap = {};
            state.dependencyGraph = null;
            state.activeStep++;
        },
        selectBuildings: (state, action: PayloadAction<Building[]>) => {
            state.selectedBuildings = action.payload;
            state.formValues = null;
            state.imageFieldMap = {};
            state.dynamicOptions = {};
            state.dependencyGraph = null;
            state.activeStep++;
        },
        setFormValues: (state, action: PayloadAction<FormValuesDto>) => {
            state.formValues = action.payload;
        },
        setImagesForField: (state, action: PayloadAction<{ field: string; images: string[] }>) => {
            state.imageFieldMap[action.payload.field] = action.payload.images;
        },
        clearImages: (state) => {
            state.imageFieldMap = {};
        },
        setDynamicOptions: (state, action: PayloadAction<{ key: string; options: OptionDto[] }>) => {
            const { key, options } = action.payload;
            state.dynamicOptions[key] = options;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(loadDocumentTemplates.pending, (state) => {
            state.templatesResponse = null;
        });
        builder.addCase(loadDocumentTemplates.fulfilled, (state, action) => {
            state.templatesResponse = action.payload;
        });
        builder.addCase(loadDocumentTemplates.rejected, (state, action) => {
            state.errorMessage = isProblemDetailTextOr(action.payload, 'Failed loading the templates');
        });
        builder.addCase(loadBuildings.fulfilled, (state, action) => {
            state.buildings = action.payload.buildings;
        });
        builder.addCase(loadBuildings.rejected, (state, action) => {
            state.errorMessage = isProblemDetailTextOr(action.payload, 'Loading buildings failed');
        });
        builder.addCase(submitForm.pending, (state) => {
            state.activeStep++;
            state.generationStatus = GenerationStatus.GENERATING;
            state.generationResponse = null;
        });
        builder.addCase(submitForm.fulfilled, (state, action) => {
            state.generationStatus = GenerationStatus.SUCCESS;
            state.generationResponse = action.payload;
        });
        builder.addCase(submitForm.rejected, (state, action) => {
            state.generationStatus = GenerationStatus.FAILED;

            if (isProblemDetailCode<GenerateDocumentValidationFailedDto>(action.payload, 'VALIDATION_ERROR')) {
                state.validationErrors = action.payload.errors;
            } else if (isProblemDetail(action.payload)) {
                state.generationError = action.payload;
            } else {
                state.errorMessage = `Unknown error occurred during document generation: ${JSON.stringify(action.payload)}`;
            }
        });
        builder.addCase(getPrefilledForm.pending, (state) => {
            state.prefilledForm = null;
        });
        builder.addCase(getPrefilledForm.fulfilled, (state, action) => {
            state.prefilledForm = action.payload;
        });
        builder.addCase(loadDependencyGraph.fulfilled, (state, action) => {
            state.dependencyGraph = action.payload.dependencyGraph;
        });
        builder.addCase(getPrefilledForm.rejected, (state, action) => {
            state.errorMessage = isProblemDetailTextOr(action.payload, 'failed to get prefill values');
        });
    },
});

export const {
    resetState,
    goBack,
    selectTemplate,
    selectBuildings,
    setFormValues,
    setImagesForField,
    clearImages,
    setDynamicOptions,
} = documentGenerationNewDocumentSlice.actions;

export default documentGenerationNewDocumentSlice.reducer;
